import * as _ from 'lodash';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, RowNode, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { IListItem, IPreselectDonorInfo, IRecipient } from '@nkr/models/index';
import { Actions, IRowData } from './models';
import { DonorPreselectReviewStatus, DonorType, MatchStatus, PotentialDonorCategory, PreSelectSection, PreSelectSubSection } from '@nkr/enums/index';
import { fileTextO } from 'react-icons-kit/fa/fileTextO';
import { commentO } from 'react-icons-kit/fa/commentO';
import { lock } from 'react-icons-kit/fa/lock';
import { Glyphicon, Label, OverlayTrigger, Popover } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { RendererHelper } from '@nkr/services/renderer-helper';
import { useMediaQuery } from 'react-responsive';
import { renderArteriesInfoIcon, renderBmtMmInfoIcon, renderLkpdiInfoIcon, renderStatusInfoIcon } from '../donor-pre-select/overlays';
import OverlayMenuButton from '@nkr/components/overlay-menu-button';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ReviewStatus from './review-status';
import Icon from 'react-icons-kit';
import PopoverTemplate from '@nkr/components/popover-template';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import LkdpiInfo from '@nkr/components/lkdpi-info';
import HlaMatches from '@nkr/views/match/hla-matches';
import AlleleMatch from '@nkr/components/allele-match';
import usePrevious from '@nkr/services/use-previous';

const CustomNoRowsOverlay = (props: { message: string }) => {
    return (
        <div style={{
            padding: '1px 10px',
            borderRadius: 4,
            backgroundColor: '#d7edf8',
            color: 'black',
            display: 'inline-block',
            border: '1px solid #d7edf8',
            fontSize: 12,
        }}>
            <h5 style={{ margin: '4px 0' }}>{props.message}</h5>
        </div>
    );
};

interface IProps {
    list: IRowData[],
    pairedDonors: IRowData[],
    recipient: IRecipient,
    centerId: number,
    pendingMatchHistoryDonorId: number,
    section: number,
    subSection: number,
    groupIsSuper: boolean,
    statusFilter: number,
    donorPreselectReviewStatusList: IListItem[],
    isPreselectPage?: boolean,
    onAction: (recipientId: number, donorId: number, action: Actions) => void,
    onSelectAll: () => void,
    onSelected: (recipientId: number, donorId: number, checked: boolean) => void,
    onReviewStatusChange: (recipientId: number, donorId: number, reviewType: DonorPreselectReviewStatus) => void
}

const Grid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [menuDisplayed, setMenuDisplayed] = useState(false);
    const previousSection = usePrevious(props.section);
    const previousSubSection = usePrevious(props.subSection);

    useEffect(() => {
        if (gridApi != null && (previousSection != props.section || previousSubSection != props.subSection)) {
            let l = props.list.filter(x => x.isPairedDonor == false);

            if (l.length > 0) {
                let firstNode = gridApi.getDisplayedRowAtIndex(0);
                gridApi.ensureNodeVisible(firstNode, 'top');
            }
        }
    }, [gridApi, props.list, props.section, props.subSection]);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        setGridApi(e.api);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const getOverlayText = () => {
        let map = {
            [PreSelectSection.PotentialDonors]: "Potential Donors",
            [PreSelectSection.AvoidConflict]: "Avoid Conflict Donors",
            [PreSelectSection.InMatch]: "In Match Donors",
            [PreSelectSection.BioMatch]: "Bio Match Donors",
            [PreSelectSection.All]: "Donors"
        };

        let subMap = {
            [PreSelectSubSection.All]: "All",
            [PreSelectSubSection.Pending]: "Pending",
            [PreSelectSubSection.Accepted]: "Accepted",
            [PreSelectSubSection.Declined]: "Declined",
        }

        return `There are no ${map[props.section]} in the ${subMap[props.subSection]} section at this time...`
    }

    const getStatus = (item: IRowData) => {
        if (item.donor.isReEvalNeeded) {
            return { description: 'Re-Eval', style: 'warning' };
        } else if (item.donor.isAccepted && item.donor.hasAcceptableXm) {
            return { description: 'XM Neg', style: 'success' };
        } else if (item.donor.hasPendingXm && item.donor.isAccepted != false) {
            return { description: 'XM Pending', style: 'warning' };
        } else if (item.donor.isAccepted && item.donor.isFullyConfident) {
            return { description: 'Confident', style: 'success' };
        } else if (item.donor.isAccepted && (item.donor.isFullyConfident == false || item.donor.isFullyConfident == null)) {
            return { description: 'Accepted', style: 'info' };
        }

        return item.donor.isAccepted == null ? { description: 'Pending', style: 'default' } : { description: 'Declined', style: 'danger' };
    };

    const getWhichKidney = (donor: IPreselectDonorInfo) => {
        let arteryCount = donor.numberOfArteries;
        let arteryLabel = arteryCount > 1 ? 'arteries' : 'artery';

        switch (donor.whichKidney) {
            case 'Left': return <p>Left kidney with ({arteryCount}) {arteryLabel}</p>;
            case 'Right': return <p>Right kidney with ({arteryCount}) {arteryLabel}</p>;
            case 'L-opt': return <p>Left kidney with ({arteryCount}) {arteryLabel} but right kidney is available</p>;
            case 'R-opt': return <p>Right kidney with ({arteryCount}) {arteryLabel} but left kidney is available</p>;
        }
    };

    const getOverlayForStatus = (data: IRowData) => {
        return (
            <PopoverTemplate>
                <div style={{ minWidth: 400 }}>
                    {data.donorNote != null && (
                        <>
                            <div><strong>Donor Note</strong></div>
                            <div>{data.donorNote != null ? data.donorNote.note : ''}</div>
                        </>
                    )}

                    {data.matchNote != null && (
                        <>
                            <div><strong>Match Note</strong></div>
                            <div>{data.matchNote != null ? data.matchNote.note : ''}</div>
                        </>
                    )}
                </div>
            </PopoverTemplate>
        );
    };

    const getResponseDateLabel = (data: IRowData) => {
        let bsStyle = null;
        let style = null;

        let daysLate = data.responseDeadLineDate != null
            ? Math.ceil((new Date().getTime() - new Date(data.responseDeadLineDate).getTime()) / (1000 * 3600 * 24))
            : null;

        if (daysLate < 0) {
            bsStyle = 'success';
            style = { backgroundColor: '#5db75e', color: 'white' };
        }

        if (daysLate == 0) {
            style = { backgroundColor: '#FF8633', color: 'white' };
        }

        if (daysLate > 0) {
            bsStyle = 'danger';
            style = { backgroundColor: '#c9372e', color: 'white' };
        }

        return <Label bsStyle={bsStyle} style={style}>{data.responseDeadLineDate}</Label>;
    };

    const getGridHeight = () => {
        const rowHeight = 42;
        const headerHeight = 48;
        const pinnedRowHeight = pairedDonors.length > 0 ? pairedDonors.length * rowHeight : 0;
        const maxVh = props.isPreselectPage ? 0.40 : 0.58;
        const maxHeight = window.innerHeight * maxVh;

        // use potentialDonors not props.list so filtered count is accurate
        const rowCount = potentialDonors.length;

        if (!rowCount) {
            return `${maxHeight}px`;
        }

        const calculated = (rowCount * rowHeight) + headerHeight + pinnedRowHeight;

        // ensure minimum height always shows at least 1 row + header + pinned
        const minHeight = headerHeight + rowHeight + pinnedRowHeight;

        return `${Math.min(Math.max(calculated, minHeight), maxHeight)}px`;
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleSelectAll = () => {
        props.onSelectAll();
    };

    const handleDonorSelected = (recipientId: number, donorId: number, checked: boolean) => {
        props.onSelected(recipientId, donorId, checked);
    };

    const handleAction = (donorId: number, action: Actions) => {
        props.onAction(props.recipient.id, donorId, action);
    };

    const isBigScreen = useMediaQuery({ minWidth: 1900 });
    const isRetinaBigScreen = useMediaQuery({ minResolution: '2dppx', minWidth: 2000 });

    const columns: ColDef[] = [
        {
            headerName: 'R#',
            width: 45,
            sortable: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as IRowData;

                if (row.isPairedDonor == true) {
                    return null;
                }

                return <>{params.value}</>
            },
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            headerComponentFramework: () => <input type='checkbox' style={{ margin: 0 }} checked={false} onClick={handleSelectAll} />,
            headerName: '',
            field: 'isSelected',
            width: 40,
            headerClass: 'list-header-select-all',
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as IRowData;

                if (row.isPairedDonor == false) {
                    return <input type='checkbox' style={{ margin: 0 }} checked={row.isSelected} onClick={(e: React.FormEvent<HTMLInputElement>) => handleDonorSelected(props.recipient.id, row.donor.id, e.currentTarget.checked)} />;
                }

                return null;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            headerName: 'Donor Center',
            field: 'donor.centerAlias',
            minWidth: props.isPreselectPage ? 50 : 90,
            cellClass: isBigScreen || isRetinaBigScreen ? 'list-cell' : 'list-cell-small',
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as IRowData;

                if (row.donor.rdnCenterId > 0) {
                    return <>
                        {isBigScreen ?
                            <>{row.donor.centerAlias} (<span style={{ color: !row.isPairedDonor ? 'red' : 'white' }}>{row.donor.rdnCenterAlias}</span>)</>
                            :
                            <>
                                <div>{row.donor.centerAlias}</div>
                                <div style={{ fontSize: 10 }}>(<span style={{ color: !row.isPairedDonor ? 'red' : 'white' }}>{row.donor.rdnCenterAlias}</span>)</div>
                            </>
                        }
                    </>;
                } else {
                    return <>{row.donor.centerAlias}</>;
                }
            },
            suppressSizeToFit: false
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            headerName: 'Donor Alias',
            field: 'donor.alias',
            minWidth: props.isPreselectPage ? 50 : 90,
            cellClass: isBigScreen || isRetinaBigScreen ? 'list-cell' : 'list-cell-small',
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as IRowData;

                if (props.groupIsSuper) {
                    return <a href={`center/${row.donor.centerId}/donor/${row.donor.id}/profile/donor-data`} target='_blank' style={{ color: row.isPairedDonor ? 'white' : null }}>{row.donor.alias}</a>;
                } else {
                    return <>{row.donor.alias}</>;
                }
            },
            suppressSizeToFit: false
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Type</SortableHeaderComponent>,
            headerName: 'Donor Type',
            field: 'donor.pairMatchPower',
            minWidth: props.isPreselectPage ? 20 : 55,
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as IRowData;

                if (row.donor.donorTypeId == DonorType.DirectedDonor) {
                    return <>PD</>;
                }

                return (
                    <OverlayTrigger rootClose={false} trigger='hover' overlay={<Popover><div>Chain Starter</div></Popover>}>
                        <div>CS</div>
                    </OverlayTrigger>
                )
            },
            valueGetter: (params: ValueGetterParams) => {
                let row = params.data as IRowData;

                if (row.donor.donorTypeId == DonorType.DirectedDonor) {
                    return 'PD';
                }

                return 'CS';
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>State</SortableHeaderComponent>,
            headerName: 'State',
            width: 60,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRowData;

                // centerState already resolves the RDN-center-over-donor-center fallback and the
                // type-1 address lookup that used to happen here, so the whole nested Center
                // object and its address collection no longer travel with every row.
                return data.donor.centerState;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
            headerName: 'ABO',
            field: 'donor.fullBloodType',
            width: 45,

        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
            headerName: 'Age',
            field: 'donor.age',
            width: 50,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wt</SortableHeaderComponent>,
            headerName: 'Wt',
            field: 'donor.weight',
            width: 50,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} overlay={renderArteriesInfoIcon()}>FAnat</SortableHeaderComponent>,
            headerName: 'FAnat',
            width: 65,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                if (params.value != '--') {
                    return (
                        <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={<PopoverTemplate>{getWhichKidney(data.donor)}</PopoverTemplate>}>
                            <a style={{ color: data.isPairedDonor ? 'white' : '#337ab7' }}>{params.value}</a>
                        </OverlayTrigger>
                    );
                }

                return <>{params.value}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRowData;

                if (data.donor.whichKidney != '' && data.donor.whichKidney != 'DNK') {
                    let results = `${data.donor.whichKidneyAbbreviated}${data.donor.numberOfArteries}`;

                    if (data.donor.whichKidney == 'L-opt') {
                        results += '(R)';
                    }

                    if (data.donor.whichKidney == 'R-opt') {
                        results += '(L)';
                    }

                    return results;
                }

                return '--';
            },
            sortingOrder: ['asc']
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>CC</SortableHeaderComponent>,
            headerName: 'CC',
            field: 'donor.creatinineClearance',
            width: 50,
        },
        {
            colId: 'ebv',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='EBV'>E</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                return RendererHelper.renderPositiveNegative(data.donor.ebv, data.isPairedDonor);
            },
            headerName: 'EBV',
            field: 'donor.ebv',
            width: 45,
            headerClass: 'th-donor',
        },
        {
            colId: 'cmv',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='CMV'>C</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                return RendererHelper.renderPositiveNegative(data.donor.cmv, data.isPairedDonor);
            },
            headerName: 'CMV',
            field: 'donor.cmv',
            width: 45,
            headerClass: 'th-donor',
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>HLA</SortableHeaderComponent>,
            headerName: 'HLA',
            field: 'totalHlaPoints',
            width: 55,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                let elem = (
                    <HlaMatches
                        donor={data.donor}
                        recipient={props.recipient}
                        compatibilityResult={data}
                        showAvoidsList={true}
                        showSmaller={true}
                    />
                )

                return (
                    <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={<PopoverTemplate width={'650px'}>{elem}</PopoverTemplate>}>
                        <a style={{ color: data.isPairedDonor ? 'white' : '#337ab7' }}>{params.value}</a>
                    </OverlayTrigger>
                )
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} overlay={renderLkpdiInfoIcon()}>LKDPI</SortableHeaderComponent>,
            headerName: 'LKDPI',
            field: 'lkdpiInfo.lkdpiIndex',
            width: 80,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                return (
                    <LkdpiInfo donor={data.donor} recipient={props.recipient} info={data.lkdpiInfo} placement='bottom' color={data.isPairedDonor ? 'white' : null} />
                )
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet MM</SortableHeaderComponent>,
            headerName: 'Eplet MM',
            field: 'singleEpletMmRisk.sortValue',
            width: 85,
            cellClass: 'list-cell-full',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                let row = params.data as IRowData;
                return (
                    <SingleEpletMmRisk data={data.singleEpletMmRisk} centerId={props.centerId} isAdmin={false} placement='bottom' />
                )
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} overlay={renderBmtMmInfoIcon()}>Allele M</SortableHeaderComponent>,
            headerName: 'Allele Match',
            field: 'bmtMm',
            width: 90,
            cellRendererFramework: (params: ICellRendererParams) => <AlleleMatch bmtMm={params.value} displayAsInline={false} />
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Avoids</SortableHeaderComponent>,
            headerName: 'Avoids',
            width: 65,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                return <Label bsStyle={params.value > 0 ? 'warning' : 'default'} title={_.join(data.failedAvoids)}>{params.value}</Label>;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRowData;
                return data.failedAvoids.length;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Notes</SortableHeaderComponent>,
            headerName: 'Notes',
            width: 55,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                let medicalComments = data.donor.medicalComments ?? '' != '' ? <><h4>Medical Comments</h4><div>{data.donor.medicalComments}</div></> : <></>
                let donorCtAbnormalities = data.donor.donorCtAbnormalities ?? '' != '' ? <><h4>Donor Abnormalities</h4><div>{data.donor.donorCtAbnormalities} </div></> : <></>

                let comments = <>{medicalComments}{donorCtAbnormalities}</>

                if (data.donorNote != null || data.matchNote != null) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>
                                <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={<PopoverTemplate>{comments}</PopoverTemplate>}>
                                    <Icon icon={fileTextO} />
                                </OverlayTrigger>
                            </div>
                            <div>
                                <OverlayTrigger rootClose placement='left' trigger='click' overlay={getOverlayForStatus(data)}>
                                    <Icon icon={commentO} />
                                </OverlayTrigger>
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={<PopoverTemplate>{comments}</PopoverTemplate>}>
                            <Icon icon={fileTextO} />
                        </OverlayTrigger>
                    );
                }
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Match</SortableHeaderComponent>,
            headerName: 'Match',
            width: 55,
            // sortingOrder: ['desc'],
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                let title = '';
                let color = '';

                if (data.donor.statusId == MatchStatus.MatchOffered) {
                    title = 'This donor has a match offer that is still pending acceptance';
                    color = 'orange';
                }

                if (data.donor.statusId > MatchStatus.MatchOffered) {
                    title = 'This donor is in a match that has already been commenced';
                    color = 'red';
                }

                return (
                    <>
                        {data.donor.statusId > MatchStatus.NeedsMatch && (
                            <div title={title}>
                                <Icon icon={lock} style={{ color }} />
                            </div>
                        )}
                    </>
                );
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRowData;
                let results = -1;

                if (data.donor.statusId > MatchStatus.NeedsMatch) {
                    results = 10;

                    if (data.donor.statusId == MatchStatus.MatchOffered) {
                        results += 10;
                    }

                    if (data.donor.statusId > MatchStatus.MatchOffered) {
                        results += 20;
                    }
                }

                return results;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} overlay={renderStatusInfoIcon()}>Status</SortableHeaderComponent>,
            headerName: 'Status',
            width: 95,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                let status = getStatus(data);

                return (
                    <>{data.isPairedDonor == false ? <Label bsStyle={status.style}>{status.description}</Label> : <>N/A</>}</>
                );
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRowData;
                return getStatus(data).description;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Review Requested</SortableHeaderComponent>,
            headerName: 'Review Requested',
            width: 125,
            hide: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                if (data.isReviewRequested)
                    return (
                        <Glyphicon glyph='ok' />
                    );

                return null;
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Response Deadline</SortableHeaderComponent>,
            headerName: 'Response Deadline',
            width: 125,
            hide: !props.isPreselectPage,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                return (
                    <>{getResponseDateLabel(data)}</>);
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><div title='This column will not affect your preselect acceptance % and is only a flag for your internal tracking'>Review</div></SortableHeaderComponent>,
            headerName: 'Review?',
            width: 70,
            cellClass: 'list-cell-full',
            field: 'reviewStatus',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;

                if (data.isPairedDonor == false) {
                    return (
                        <ReviewStatus
                            recipientId={props.recipient.id}
                            donorId={data.donor.id}
                            reviewStatus={data.reviewStatus}
                            reviewStatusList={props.donorPreselectReviewStatusList}
                            onToggle={handleMenuToggle}
                            onChange={props.onReviewStatusChange}
                        />
                    );
                }

                return null;
            },
            valueGetter: (params: ValueGetterParams) => {
                let reviewStatus = props.donorPreselectReviewStatusList.find(x => x.id == params.data.reviewStatus);
                return reviewStatus != null ? reviewStatus.abbreviation : 'Z';
            }
        },
        {
            headerName: '',
            field: 'id',
            width: 95,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRowData;
                let donor = data.donor;
                let url = '';

                if (data.isPairedDonor == false && props.pendingMatchHistoryDonorId == donor.id) {
                    url = `/center/${props.recipient.centerId}/recipient/evaluate-match/${data.matchOfferId}`;
                }

                if (data.isPairedDonor == false) {
                    let menu = [
                        { id: donor.id, action: Actions.NotSelectable, include: props.pendingMatchHistoryDonorId == donor.id, selectable: false, url, element: <><Label bsStyle='warning' style={{ fontSize: '100%' }}> Pending Match Decision</Label></> },
                        { id: donor.id, action: Actions.AcceptableFullyConfident, include: props.pendingMatchHistoryDonorId != donor.id, selectable: true, element: <span style={{ color: 'green' }}>Acceptable</span> },
                        { id: donor.id, action: Actions.NotAcceptable, include: props.pendingMatchHistoryDonorId != donor.id, selectable: true, element: <span style={{ color: 'red' }}>NOT Acceptable</span> },
                        { id: donor.id, action: Actions.Evaluate, include: true, selectable: true, element: <span style={{ color: 'blue' }}>Evaluate</span> },
                    ];

                    return (
                        <OverlayMenuButton
                            bsStyle='primary'
                            bsSize='xs'
                            title='Actions'
                            menu={menu}
                            includeCaret={true}
                            placement='left'
                            includeArrow={true}
                            onToggle={handleMenuToggle}
                            onSelect={handleAction}
                        />
                    );
                }

                return null;
            }
        }
    ];

    const noOverlayText = getOverlayText();

    const pairedDonors = props.pairedDonors;

    const potentialDonors = props.list.filter(x => {
        let isNotPairedDonor = x.isPairedDonor == false
        let isStatusFilter = props.statusFilter > 0 ? x.reviewStatus == props.statusFilter : true;

        return isNotPairedDonor && isStatusFilter;
    });

    const columnDefs = useMemo(() => columns, [props.recipient]);
    const rowData = useMemo(() => potentialDonors, [potentialDonors, props.statusFilter, props.section, props.subSection]);



    return (
        <GridWrapper className="ag-theme-bootstrap" style={{ height: getGridHeight(), marginBottom: 0 }}>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                rowData={rowData}
                pinnedTopRowData={pairedDonors}
                headerHeight={28}
                rowHeight={30}
                suppressCellSelection={true}
                suppressRowClickSelection={true}
                suppressContextMenu={true}
                enableCellTextSelection={true}
                alwaysShowVerticalScroll={true}
                immutableData={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onBodyScroll={onBodyScroll}
                onGridSizeChanged={onGridSizeChanged}
                onRowDataChanged={onRowDataChanged}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                frameworkComponents={{
                    customNoRowsOverlay: CustomNoRowsOverlay
                }}
                noRowsOverlayComponent={'customNoRowsOverlay'}
                noRowsOverlayComponentParams={{
                    message: noOverlayText
                }}
                getRowStyle={(props: { api: GridApi, data: IRowData, node: RowNode }) => {
                    if (props.data.isPairedDonor) {
                        return { backgroundColor: 'darkgreen', color: 'white' };
                    }

                    return { fontSize: isBigScreen ? '14px !important' : '12px !important' };
                }}
                getRowNodeId={(data: IRowData) => data.donor.id.toString()}
            >
            </AgGridReact>
        </GridWrapper >
    );
}

export default Grid;